import React, { useState, useEffect } from "react";
import {
  Tab,
  Tabs,
  Grid2,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormLabel,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RelationshipRevenueCard from "./RelationshipRevenueCard";
import CompanyProfileCard from "./CompanyProfileCard";
import UpcomingPaymentsCard from "./UpcomingPaymentsCard";
import ProductHoldingsCard from "./ProductHoldingsCard";
import SignalsCard from "./SignalsCard";
import OverdueCard from "./OverdueCard";

import { tableData } from "../assets/tableData";
import RelationshipRevenueTable from "./RelationshipRevenueTable";
import CompanyProfileSubTab from "./CompanyProfileSubTab";
import { useClientContext } from "../context/ClientContext";
import { companyProfileTabData } from "../assets/companyProfile";
import { clientGroups } from "../assets/clientData";
import LoanAccountsTable from "./LoanAccountsTable";
import ThirdPartyFundTable from "./ThirdPartyFundTable";
import LoanAccLevel3Table from "./LoanAccLevel3Table";
import { robbonKPIValues } from "../assets/topRibbonKPIs";
import { formatCurrency } from "../utils/helper";
import { relationshipRevenueData } from "../assets/relationshipRevenue";
import ClosedLoopTree from "./ClosedLoopTree";
import ClosedLoopTreeIncoming from "./ClosedLoopTreeIncoming";
import ClosedLoopTreeOutgoing from "./ClosedLoopTreeOutgoing";
import { transactions } from "../assets/transactions";
import CustomerTransactionCard from "./CustomerTransactionCard";

const ClientMetric = () => {
  const [showTable, setShowTable] = useState(false);
  const [showCompanySubTab, setShowCompanySubTab] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedKPIs, setSelectedKPIs] = useState([
    "creditLimit",
    "creditUtil",
    "liabilities",
    "creditOutstanding",
    "productEngagements",
    "walletSize",
    "walletShare",
    "percentageClosedLoop",
  ]);
  const kpiOptions = [
    { id: "creditLimit", label: "Total Credit Limit" },
    { id: "creditUtil", label: "Total Credit Utilization" },
    { id: "liabilities", label: "Total Liabilities" },
    { id: "creditOutstanding", label: "Total Credit Outstanding" },
    { id: "productEngagements", label: "New Product & Services Engagements" },
    { id: "walletSize", label: "Wallet Size" },
    { id: "walletShare", label: "Wallet Share" },
    { id: "percentageClosedLoop", label: "Percentage Closed Loop" },
  ];
  const { clientName, clientGroup } = useClientContext();
  const [companyProfileData, setCompanyProfileData] = useState([]);
  const [level, setLevel] = useState("L1");
  const [ribbonData, setRibbonData] = useState([]);
  const [revenueTableData, setRevenueTableData] = useState([]);
  const [transactionData, setTransactionData] = useState([]);

  useEffect(() => {
    if (clientGroup && clientName) {
      const matchingData = companyProfileTabData.filter(
        (company) =>
          company["Group Company Name"] ===
            clientGroups.find((group) => group.group_name === clientGroup)
              ?.label && company["Company Name"] === clientName
      );

      const ribbonData = robbonKPIValues.filter(
        (data) =>
          data.company_name === clientName &&
          data.group_company ===
            clientGroups.find((group) => group.group_name === clientGroup)
              ?.label
      );

      const revenueTableDataSet = relationshipRevenueData.filter(
        (data) =>
          data.company_name === clientName &&
          data.group_company ===
            clientGroups.find((group) => group.group_name === clientGroup)
              ?.label
      );

      const matchingTxnData = transactions.filter(
        (data) =>
          data.company_name === clientName &&
          data.group_company ===
            clientGroups.find((group) => group.group_name === clientGroup)
              ?.label
      );

      if (matchingData.length > 0) {
        setCompanyProfileData(matchingData);
      }

      if (ribbonData.length > 0) {
        setRibbonData(ribbonData[0]);
      }

      if (revenueTableDataSet.length > 0) {
        setRevenueTableData(revenueTableDataSet[0]);
      }

      if (matchingTxnData.length > 0) {
        setTransactionData(matchingTxnData[0]);
      }
    }
  }, [clientGroup, clientName]);

  const handleKPIChange = (event) => {
    setSelectedKPIs(event.target.value);
  };

  const renderTabContent = () => {
    switch (selectedTab) {
      case 0:
        return showCompanySubTab ? (
          <CompanyProfileSubTab
            setShowCompanySubTab={setShowCompanySubTab}
            boardOfDirectors={companyProfileData[0]?.km_board_of_directors}
            boardOfCommissioners={
              companyProfileData[0]?.km_board_of_commissioners
            }
            groupCompanies={companyProfileData[0]?.other_group_companies}
          />
        ) : (
          <CompanyProfileCard
            setShowCompanySubTab={setShowCompanySubTab}
            data={companyProfileData}
          />
        );
      case 1:
        return showTable ? (
          <RelationshipRevenueTable
            tableData={revenueTableData.tableData || []}
            dialogLabel={"Revenue"}
            setShowTable={setShowTable}
          />
        ) : (
          <RelationshipRevenueCard setShowTable={setShowTable} />
        );
      case 2:
        return (
          <>
            {level.includes("L1") && (
              <ProductHoldingsCard setLevel={setLevel} />
            )}
            {level.includes("L2.1") && (
              <ThirdPartyFundTable setLevel={setLevel} tableData={tableData} />
            )}
            {level.includes("L2.2") && (
              <LoanAccountsTable
                setLevel={setLevel}
                dialogLabel={"Loan Accounts"}
              />
            )}
            {level.includes("L3") && (
              <LoanAccLevel3Table setLevel={setLevel} tableData={tableData} />
            )}
          </>
        );
      case 3:
        return <CustomerTransactionCard data={transactionData} />;
      case 4:
        return <ClosedLoopTreeIncoming />;
      case 5:
        return <ClosedLoopTreeOutgoing />;
      case 6:
        return <SignalsCard />;
      case 7:
        return (
          <div className="text-xs text-gray-600 m-4">Work in Progress</div>
        );
      case 8:
        return (
          <div className="text-xs text-gray-600 m-4">Work in Progress</div>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <div
        className="flex items-center justify-center gap-6 p-2 rounded"
        style={{ boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.8)" }}
      >
        {/* testing source scroll to page */}
        {/* <div 
          className="flex items-center justify-center gap-2 cursor-pointer"
          onClick={() => {
            const link = "https://docs.google.com/document/d/1PosOWfCzcFTG8lGU1slLgF-JySKYq_S3/edit?usp=drive_link&ouid=113774461930656718899&rtpof=true&sd=true#page=5"; // Replace with actual link
            const newWindow = window.open(link, '_blank');
            if (newWindow) {
              newWindow.addEventListener('load', () => {
                newWindow.scrollTo({
                  top: link.scrollPosition || 0,
                  behavior: 'smooth'
                });
              });
            }
          }}
        >
          href
        </div> */}

        <div className="flex flex-col w-full">
          <div className="flex gap-6 overflow-x-auto" style={{ minWidth: 0 }}>
            <FormControl
              variant="standard"
              sx={{
                m: 1,
                width: 150,
                flexShrink: 0,
                minWidth: 120,
                "& .MuiInput-root": {
                  "&:before": {
                    borderBottom: "1px solid #00555C",
                  },
                  "&:hover:not(.Mui-disabled):before": {
                    borderBottom: "2px solid #00555C",
                  },
                  "&.Mui-focused:after": {
                    borderBottom: "2px solid #00555C",
                  },
                },
              }}
            >
              <Select
                multiple
                value={selectedKPIs}
                onChange={handleKPIChange}
                renderValue={() => "Metric Selection"}
                size="small"
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "#00555C",
                  "& .MuiSelect-select": {
                    paddingY: 0.5,
                  },
                  "& .MuiSelect-icon": {
                    color: "#00555C",
                  },
                }}
                IconComponent={ExpandMoreIcon}
              >
                {kpiOptions.map((kpi) => (
                  <MenuItem
                    key={kpi.id}
                    value={kpi.id}
                    sx={{ fontSize: "0.5rem" }}
                  >
                    <Checkbox checked={selectedKPIs.includes(kpi.id)} />
                    <ListItemText primary={kpi.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedKPIs.includes("creditLimit") && (
              <div className="flex flex-col items-center justify-center min-w-fit">
                <div className="font-bold text-xs">Total Credit Limit</div>
                <div className="text-main text-xs">
                  {formatCurrency(ribbonData?.total_credit_limit)} IDR
                </div>
              </div>
            )}
            {selectedKPIs.includes("creditUtil") && (
              <div className="flex flex-col items-center justify-center min-w-fit">
                <div className="font-bold text-xs">
                  Total Credit Limit Utilization
                </div>
                <div className="text-main text-xs">
                  {ribbonData?.total_credit_limit_utilization?.toFixed(1) ||
                    "..."}
                  %
                </div>
              </div>
            )}
            {selectedKPIs.includes("creditOutstanding") && (
              <div className="flex flex-col items-center justify-center min-w-fit">
                <div className="font-bold text-xs">
                  Total Credit Outstanding
                </div>
                <div className="text-main text-xs">
                  {formatCurrency(ribbonData?.total_credit_outstanding)} IDR
                </div>
              </div>
            )}
            {selectedKPIs.includes("liabilities") && (
              <div className="flex flex-col items-center justify-center min-w-fit">
                <div className="font-bold text-xs">Total Liabilities</div>
                <div className="text-main text-xs">
                  {formatCurrency(ribbonData?.total_liabilities)} IDR
                </div>
              </div>
            )}
            {selectedKPIs.includes("productEngagements") && (
              <div className="flex flex-col items-center justify-center min-w-fit">
                <div className="font-bold text-xs">
                  New Product & Services Engagements (Current FY)
                </div>
                <div className="text-main text-xs">
                  {ribbonData?.new_product_and_services_engagements}
                </div>
              </div>
            )}
            {selectedKPIs.includes("walletSize") && (
              <div className="flex flex-col items-center justify-center min-w-fit">
                <div className="font-bold text-xs">Wallet Size</div>
                <div className="text-main text-xs">
                  {ribbonData?.wallet_size} Bn IDR
                </div>
              </div>
            )}
            {selectedKPIs.includes("walletShare") && (
              <div className="flex flex-col items-center justify-center min-w-fit">
                <div className="font-bold text-xs">Wallet Share</div>
                <div className="text-main text-xs">
                  {ribbonData?.wallet_share?.toFixed(1) || "..."} %
                </div>
              </div>
            )}
            {selectedKPIs.includes("percentageClosedLoop") && (
              <div className="flex flex-col items-center justify-center min-w-fit">
                <div className="font-bold text-xs">Percentage Closed Loop</div>
                <div className="text-main text-xs">
                  {ribbonData?.percent_closed_loop?.toFixed(1) || "..."} %
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Vertical Tabs Layout */}
      <Grid2 container>
        <Grid2 item size={2.4} py={0.5} pr={0.5}>
          <Tabs
            orientation="vertical"
            variant="scrollable"
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            sx={{
              borderRight: 1,
              borderColor: "divider",
              height: "60vh", // Set fixed height
              maxHeight: "60vh", // Set max height
              overflow: "auto", // Enable scrolling
              "& .MuiTabs-scroller": {
                overflow: "auto !important", // Force scrollbar to show
              },
              "& .MuiTab-root": {
                alignItems: "flex-start",
                textAlign: "left",
                fontSize: "0.7rem",
                fontWeight: "bold",
                textTransform: "capitalize",
                minHeight: "48px",
                padding: "8px 16px",
              },
            }}
          >
            <Tooltip placement="right" title="Comprehensive summary of client's key details including business information, industry classification, ownership structure, subsidiaries, key management personnel, etc" >
              <Tab label="Company Profile" />
            </Tooltip>
            <Tooltip placement="right" title="A measure of the total revenue generated from a corporate client, including both interest income and non-interest income">
              <Tab label="Relationship Revenue" />
            </Tooltip>
            <Tooltip placement="right" title="The comprehensive portfolio of financial products and services that a corporate client holds with the bank. This includes all active products such as deposits, loans, trade finance instruments, treasury solutions, and other specialized banking services">
              <Tab label="Product Holdings" />
            </Tooltip>
            <Tooltip placement="right" title="Overview of all incoming and outgoing financial movements in a client’s account">
              <Tab label="Customer Transactions" />
            </Tooltip>
            <Tooltip placement="right" title="Total inflow of funds into client's accounts, including payments received from customers, deposits, and other credits">
              <Tab
                label={<i>Incoming Transactions</i>}
                sx={{
                  marginLeft: "1.25rem",
                  fontSize: "0.65rem !important",
                  minHeight: "32px !important",
                  textTransform: "none",
                }}
                disableRipple
              />
            </Tooltip>
            <Tooltip placement="right" title=" The total outflow of funds from client’s accounts, including payments to suppliers, loan repayments, and other debits">
              <Tab
                label={<i>Outgoing Transactions</i>}
                sx={{
                  marginLeft: "1.25rem",
                  fontSize: "0.65rem !important",
                  minHeight: "32px !important",
                  textTransform: "none",
                }}
                disableRipple
              />
            </Tooltip>
            <Tooltip placement="right" title="Signals indicate potential opportunity based on client's financial activity, product mix and relationship with the bank.">
              <Tab label="Signals" />
            </Tooltip>
            <Tooltip placement="right" title="Covenants (contractual obligations or conditions) that the corporate client has:
1. Covenants not yet fulfilled and are past their due date, posing potential risks to the bank's exposure
2. Covenants which are upcoming in the next 30 days"
            >
              <Tab label="Overdue/Upcoming Covenants" />
            </Tooltip>
            <Tooltip placement="right" title="Add New">
              <Tab label={<i>+ New</i>} />
            </Tooltip>
          </Tabs>
        </Grid2>

        <Grid2 item size={9.6} py={0.5} pl={0.5}>
          {renderTabContent()}
        </Grid2>
      </Grid2>
    </div>
  );
};

export default ClientMetric;
